// DiscoverPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/DiscoverPage.css";
import AuthGateModal from "../components/AuthGateModal";
import { getIdentifier } from "../utils/auth";
import PublicHeader from "../components/PublicHeader";
import "../styles/PublicHeader.css";



const API_BASE = "https://squid-app-6s2hc.ondigitalocean.app";

const ROUTE_CREATE_COLLECTION = "/upload-collection";
const ROUTE_VIEW_COLLECTIONS = "/view-collection-selector";
const ROUTE_EDIT_COLLECTIONS = "/edit-collection-selector";

function useDebouncedValue(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

/* ─────────────────────────────── CARD ─────────────────────────────── */
function DiscoverCard({ hit, canView, onRequireAuth }) {
  const navigate = useNavigate();

  const onClick = () => {
    const targetPath = `/view-collection?u=${encodeURIComponent(
      hit.ownerHandle
    )}&c=${encodeURIComponent(hit.collectionName)}`;

    const navState = { state: { selectedItemId: hit.id } };

    if (!canView) {
      onRequireAuth(targetPath, navState);
      return;
    }

    navigate(targetPath, navState);
  };

  const imageSrc =
    hit.thumbUrl ||
    hit.imageUrl ||
    hit.image ||
    hit.originalUrl ||
    (Array.isArray(hit.images) && hit.images[0]?.url) ||
    (Array.isArray(hit.media) && hit.media[0]?.url) ||
    null;

  return (
    <button
      className="discover-card"
      onClick={onClick}
      aria-label={`Open ${hit.name || "item"} from ${hit.collectionName || "collection"}`}
    >
      <div className="thumb-wrap">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={hit.altText || hit.name || "item"}
            loading="lazy"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "center",
            }}
          />
        ) : (
          <div className="thumb-fallback">no image</div>
        )}
      </div>

      <div className="meta">
        <div className="title">{hit.name || "untitled"}</div>
        {hit.ownerHandle && (
          <div className="user">
            @{hit.ownerHandle} • {hit.collectionName}
          </div>
        )}
      </div>
    </button>
  );
}

/* ─────────────────────────────── PAGE ─────────────────────────────── */
export default function DiscoverPage() {
  const navigate = useNavigate();
  const location = useLocation();


  const [identifier, setIdentifier] = useState(() => getIdentifier() || "");
  const isAuthed = !!identifier;

  const idPayload = useMemo(
    () => (id) => (id && id.includes("@") ? { email: id } : { username: id }),
    []
  );

  const [hasCollections, setHasCollections] = useState(false);

  /* sidebar (mobile) */
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  /* keep identifier in sync across login/logout + tab focus */
  useEffect(() => {
    const sync = () => setIdentifier(getIdentifier() || "");
    sync();

    window.addEventListener("focus", sync);
    window.addEventListener("storage", sync);

    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  /* close drawer on Escape */
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  /* close drawer on route change */
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.key]);

  /* ✅ load whether authed user has collections */
  useEffect(() => {
    let alive = true;

    async function loadHasCollections() {
      if (!identifier) {
        if (alive) setHasCollections(false);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/get-user`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(idPayload(identifier)),
        });

        const data = await res.json().catch(() => ({}));
        if (!alive) return;

        const has =
          !!data?.hasCollection ||
          !!data?.hasCollections ||
          (Array.isArray(data?.collections) && data.collections.length > 0);

        setHasCollections(has);
      } catch {
        if (alive) setHasCollections(false);
      }
    }

    loadHasCollections();
    return () => {
      alive = false;
    };
  }, [identifier, idPayload]);

  const go = (path) => () => {
    setMobileNavOpen(false);
    navigate(path);
  };

  const handleHome = go("/home-page");
  const handleProfile = go("/profile");
  const handleContact = go("/contact");
  const handleCreateCollection = go(ROUTE_CREATE_COLLECTION);
  const handleViewCollections = go(ROUTE_VIEW_COLLECTIONS);
  const handleEditCollections = go(ROUTE_EDIT_COLLECTIONS);

  const openLearnMore = go("/about");

  const openTour = go("/overview");

  /* search */
  const [q, setQ] = useState("");
  const dq = useDebouncedValue(q, 350);

  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [nextOffset, setNextOffset] = useState(null);

  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const abortRef = useRef(null);

  /* auth gate modal */
  const [modalOpen, setModalOpen] = useState(false);
  const [nextPath, setNextPath] = useState("/");
  const [nextState, setNextState] = useState(undefined);

  const requireAuth = (path, state) => {
    setNextPath(path);
    setNextState(state);
    setModalOpen(true);
  };

  const canLoadMore = !loadingInitial && !loadingMore && nextOffset !== null;
  const showEndCap =
    !loadingInitial && !loadingMore && nextOffset === null && items.length > 0;

  const search = async ({ reset = false, next = null } = {}) => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const isDiscoverMode = !dq.trim();
    const effectiveOffset = reset ? 0 : next ?? offset;

    if (reset) setLoadingInitial(true);
    else setLoadingMore(true);

    try {
      const params = new URLSearchParams({
        limit: "24",
        offset: String(effectiveOffset),
      });

      if (dq.trim()) params.set("q", dq);

      const res = await fetch(`${API_BASE}/search-items?${params.toString()}`, {
        method: "GET",
        signal: ctrl.signal,
      });

      const data = await res.json().catch(() => ({}));
      const serverNextOffset = data?.nextOffset ?? null;

      let incoming = (data.items || []).filter((item) => {
        const haystack = [
          item.name,
          item.artist,
          item.collectionName,
          item.ownerHandle,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return !haystack.includes("test");
      });

      if (!incoming.length && serverNextOffset !== null) {
        setNextOffset(serverNextOffset);
        search({ reset, next: serverNextOffset });
        return;
      }

      if (isDiscoverMode) incoming = shuffle(incoming);

      if (reset) {
        setItems(incoming);
        setOffset(0);
      } else {
        setItems((prev) => [...prev, ...incoming]);
        setOffset(effectiveOffset);
      }

      setNextOffset(serverNextOffset);
    } catch {
      /* no-op */
    } finally {
      if (reset) setLoadingInitial(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setItems([]);
    setOffset(0);
    setNextOffset(null);
    setLoadingInitial(true);
    search({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dq]);

  const onLoadMore = () => {
    if (nextOffset == null) return;
    search({ reset: false, next: nextOffset });
  };

  const guidanceBox = useMemo(() => {
    const isDiscoverMode = !dq.trim();
    const noResults = !items.length && !loadingInitial && !loadingMore;

    if (isDiscoverMode || noResults || loadingInitial) {
      return (
        <div className="discover-empty">
          <p></p>
          <p className="hint">
            try: &quot;children&apos;s books&quot;, &quot;lego&quot;,
            &quot;south african art&quot;
          </p>
          {!isDiscoverMode && noResults && (
            <p className="hint" style={{ marginTop: 10 }}>
              no results for “{dq}”.
            </p>
          )}
        </div>
      );
    }
    return null;
  }, [dq, items.length, loadingInitial, loadingMore]);

  return (
    <div className={`nav-shell ${mobileNavOpen ? "nav-shell--open" : ""}`}>
  
      <div
  className={`menu-overlay ${mobileNavOpen ? "open" : ""}`}
  onClick={() => setMobileNavOpen(false)}
/>

<PublicHeader
  mobileNavOpen={mobileNavOpen}
  setMobileNavOpen={setMobileNavOpen}
 showAuth={false}
/>

<aside className={`slide-menu ${mobileNavOpen ? "open" : ""}`}>
  <button
    className="menu-close"
    onClick={() => setMobileNavOpen(false)}
  >
    ×
  </button>

  <nav className="menu-nav">

    {!isAuthed ? (
      <>
        <button onClick={go("/welcome")}>welcome</button>

        <button onClick={go("/new")}>
          sign up
        </button>

        <button onClick={go("/returning")}>
          log in
        </button>

        <div className="menu-divider" />

        <button onClick={openTour}>
          see how it works
        </button>

        <button onClick={openLearnMore}>
          about the platform
        </button>
      </>
    ) : (
      <>
        <button onClick={handleHome}>
          home
        </button>

        <button onClick={handleProfile}>
          profile
        </button>

        <div className="menu-divider" />

        <button onClick={handleCreateCollection}>
          create new collection
        </button>

        {hasCollections && (
          <>
            <button onClick={handleViewCollections}>
              view my collection(s)
            </button>

            <button onClick={handleEditCollections}>
              edit my collection(s)
            </button>
          </>
        )}

        <div className="menu-divider" />

        <button onClick={handleContact}>
          contact t.c.p.
        </button>

        <button onClick={openLearnMore}>
          learn more
        </button>
      </>
    )}

  </nav>
</aside>
      <main className="discover-container">
       
    <header className="discover-header">      
          <input
            className="discover-input discover-input--hero"
            type="search"
            placeholder="search (public) items, collectors, collections…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus={false}
            spellCheck={false}
          />
        </header>

        {guidanceBox}

        {loadingInitial && (
  <div className="discover-loading-wrap" role="status" aria-live="polite">
    <div className="discover-loading-title">loading items…</div>
  </div>
)}

      <section className="discover-grid">
          {items.map((h) => (
            <DiscoverCard
              key={h.id}
              hit={h}
              canView={isAuthed}
              onRequireAuth={requireAuth}
            />
          ))}
        </section>

        <div className="discover-footer">
          {canLoadMore && (
            <button className="discover-load" onClick={onLoadMore} disabled={loadingMore}>
              {loadingMore ? "loading…" : "load more"}
            </button>
          )}
          {showEndCap && <div className="end-cap">no more results</div>}
        </div>

        <AuthGateModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          nextPath={nextPath}
          nextState={nextState}
        />
      </main>
    </div>
  );
}